<script lang="ts" setup>
import type { FetchError } from 'ofetch'

import BasicButton from '~/components/form-elements/BasicButton.vue'
import BasicInput from '~/components/form-elements/BasicInput.vue'
import BasicTextarea from '~/components/form-elements/BasicTextarea.vue'
import LabelText from '~/components/form-elements/LabelText.vue'
import RadioButtons from '~/components/form-elements/RadioButtons.vue'
import YesNoSwitch from '~/components/form-elements/YesNoSwitch.vue'
import CustomModalWindow from '~/components/modal-windows/CustomModalWindow.vue'
import { AmbassadorDirection, AmbassadorDirectionUtils } from '~/enums/ambassador-direction'
import { AmbassadorSource, AmbassadorSourceUtils } from '~/enums/ambassador-source'
import { Mask, MaskUtils } from '~/enums/mask'
import helpers from '~/helpers'
import ApiErrorHandler from '~/helpers/api-error-handler'
import type { RadioButtonChoice } from '~/types/form-elements'

const model = defineModel<boolean>()

const props = defineProps<{
  userData: any | null
}>()

type Channel = { link: string | null, subscribersCount: number | null }

const buttonTitle = 'Отправить заявку'
const emptyChannel: Channel = { link: null, subscribersCount: null }
const emptyForm = {
  channels: [{ ...emptyChannel }],
  city: null,
  comment: null,
  customSource: null,
  desiredPromoCode: null,
  directionId: null,
  email: null,
  fullName: null,
  hasAccount: false,
  password: null,
  phoneNumber: null,
  sourceId: null,
  subdirection: null
}
const directionChoices = AmbassadorDirectionUtils.sortedMembers.map(
  (x: AmbassadorDirection): RadioButtonChoice => ({ labelText: AmbassadorDirectionUtils.getName(x), value: x })
)
const maskaOptions = {
  number: MaskUtils.getMaskaOptions(Mask.Number),
  promoCode: MaskUtils.getMaskaOptions(Mask.PromoCode)
}
const maxChannels = 5
const messages = {
  channelLinkFieldCannotBeEmpty: 'Поле «Ссылка на канал» не может быть пустым.',
  channelSubscribersCountFieldCannotBeEmpty: 'Поле «Кол-во подписчиков на канале» не может быть пустым.',
  cityFieldCannotBeEmpty: 'Поле «Город» не может быть пустым.',
  customSourceField1CannotBeEmpty: 'Поле «Из какого?» не может быть пустым.',
  customSourceField2CannotBeEmpty: 'Поле «Откуда?» не может быть пустым.',
  desiredPromoCodeFieldCannotBeEmpty: 'Поле «Желаемый промокод» не может быть пустым.',
  directionIsNotSelected: 'Сфера деятельности не выбрана.',
  emailFieldCannotBeEmpty: 'Поле «Email» не может быть пустым.',
  failedToSendApplication: 'Не удалось отправить заявку.',
  fullNameFieldCannotBeEmpty: 'Поле «Ф. И. О. (полностью)» не может быть пустым.',
  passwordFieldCannotBeEmpty: 'Поле «Пароль» не может быть пустым.',
  phoneNumberFieldCannotBeEmpty: 'Поле «Номер телефона» не может быть пустым.',
  requestIsSent: 'Заявка отправлена.',
  sourceIsNotSelected: 'Не выбран вариант ответа для «Как вы узнали о нашей реферальной программе?»',
  subdirectionField1CannotBeEmpty: 'Поле «Специализация» не может быть пустым.',
  subdirectionField2CannotBeEmpty: 'Поле «Сфера деятельности» не может быть пустым.'
}
const sourceChoices = AmbassadorSourceUtils.sortedMembers.map(
  (x: AmbassadorSource): RadioButtonChoice => ({ labelText: AmbassadorSourceUtils.getName(x), value: x })
)

const { $apiHelper, $toast } = useNuxtApp()

const form = ref<any>({ ...emptyForm })
const isSending = ref(false)
const isRequestSent = ref(false)

const customSourceFieldLabelTextAndDescription = computed(
  () => {
    switch (form.value.sourceId) {
      case AmbassadorSource.EducationalCenter:
        return ['Из какого?', 'МИИН, УОМ, PreventAge, EDPRO и др.']
      case AmbassadorSource.Other:
        return ['Откуда?', null]
      default:
        return [null, null]
    }
  }
)
const customSourceFieldErrorMessage = computed(
  () => {
    switch (form.value.directionId) {
      case AmbassadorDirection.Doctor:
        return messages.customSourceField1CannotBeEmpty
      case AmbassadorDirection.Other:
        return messages.customSourceField2CannotBeEmpty
      default:
        return null
    }
  }
)
const needToShowCustomSourceField = computed(
  () => [AmbassadorSource.EducationalCenter, AmbassadorSource.Other].includes(form.value.sourceId)
)
const needToShowSubdirectionField = computed(
  () => [AmbassadorDirection.Doctor, AmbassadorDirection.Other].includes(form.value.directionId)
)
const subdirectionFieldLabelText = computed(
  () => {
    switch (form.value.directionId) {
      case AmbassadorDirection.Doctor:
        return 'Специализация'
      case AmbassadorDirection.Other:
        return 'Сфера деятельности'
      default:
        return null
    }
  }
)
const subdirectionFieldErrorMessage = computed(
  () => {
    switch (form.value.directionId) {
      case AmbassadorDirection.Doctor:
        return messages.subdirectionField1CannotBeEmpty
      case AmbassadorDirection.Other:
        return messages.subdirectionField2CannotBeEmpty
      default:
        return null
    }
  }
)

function addChannel (): void {
  form.value.channels.push({ ...emptyChannel })
}

function beforeOpen (): void {
  if (props.userData) {
    const { email, firstName, lastName, patronymic, phoneNumber } = props.userData.attributes
    emptyForm.channels = [{ ...emptyChannel }]
    form.value = {
      ...emptyForm,
      ...{ email, fullName: [lastName, firstName, patronymic].join(' ').trim(), phoneNumber }
    }
  }
}

function close (): void {
  model.value = false
}

function deleteChannel (index: number): void {
  form.value.channels.splice(index, 1)
}

async function sendApplication (): Promise<void> {
  const {
    channels,
    city,
    comment,
    customSource,
    desiredPromoCode,
    directionId,
    email,
    fullName,
    hasAccount,
    password,
    phoneNumber,
    sourceId,
    subdirection
  } = form.value
  const {
    channelLinkFieldCannotBeEmpty,
    channelSubscribersCountFieldCannotBeEmpty,
    cityFieldCannotBeEmpty,
    desiredPromoCodeFieldCannotBeEmpty,
    directionIsNotSelected,
    emailFieldCannotBeEmpty,
    failedToSendApplication,
    fullNameFieldCannotBeEmpty,
    passwordFieldCannotBeEmpty,
    phoneNumberFieldCannotBeEmpty,
    requestIsSent,
    sourceIsNotSelected
  } = messages

  const errors = [
    { condition: !fullName, message: fullNameFieldCannotBeEmpty },
    { condition: !city, message: cityFieldCannotBeEmpty },
    { condition: !email, message: emailFieldCannotBeEmpty },
    { condition: !phoneNumber, message: phoneNumberFieldCannotBeEmpty },
    { condition: directionId === null, message: directionIsNotSelected },
    { condition: needToShowSubdirectionField.value && !subdirection, message: subdirectionFieldErrorMessage.value! },
    { condition: !props.userData && !hasAccount && !password, message: passwordFieldCannotBeEmpty },
    { condition: !desiredPromoCode, message: desiredPromoCodeFieldCannotBeEmpty },
    { condition: sourceId === null, message: sourceIsNotSelected },
    { condition: needToShowCustomSourceField.value && !customSource, message: customSourceFieldErrorMessage.value! }
  ]
  errors.push(...channels.map((x: Channel): any => ({ condition: !x.link, message: channelLinkFieldCannotBeEmpty })))
  errors.push(
    ...channels.map(
      (x: Channel): any => ({ condition: !x.subscribersCount, message: channelSubscribersCountFieldCannotBeEmpty })
    )
  )

  for (const { condition, message } of errors) {
    if (condition) {
      $toast.error(message)

      return
    }
  }

  const data = {
    channels,
    city,
    comment,
    customSource: needToShowCustomSourceField.value ? customSource : null,
    desiredPromoCode,
    directionId,
    email,
    fullName,
    phoneNumber,
    sourceId,
    subdirection: needToShowSubdirectionField.value ? subdirection : null
  } as any

  if (!props.userData && !hasAccount) {
    data.password = password
  }

  const referrerPromoCode = useCookie('promoCode').value

  if (referrerPromoCode) {
    data.referrerPromoCode = referrerPromoCode
  }

  const utmParameters = helpers.getUtmParameters()

  if (utmParameters) {
    data.utmParameters = utmParameters
  }

  isSending.value = true

  try {
    await $apiHelper.promoCodeApplications.createPromoCodeApplication(data)

    isRequestSent.value = true

    $toast.success(requestIsSent)
  } catch (error) {
    ApiErrorHandler.handleFetchError(error as FetchError, failedToSendApplication)
  }

  isSending.value = false
}
</script>

<template>
  <CustomModalWindow v-model="model" size="large" @before-open="beforeOpen">
    <template #title>
      Оформление промокода
    </template>

    <template v-if="isRequestSent">
      <p>Спасибо за&nbsp;заполнение формы!</p>

      <p>Рассмотрение заявки происходит в&nbsp;течение <strong>2&nbsp;рабочих дней</strong>. Просим вас <strong>не&nbsp;дублировать запрос</strong> на&nbsp;оформление промокода.</p>

      <p>В&nbsp;случае одобрения заявки промокод появится у&nbsp;вас в&nbsp;личном кабинете.</p>
    </template>
    <template v-else>
      <Notification class="mb-6" is-small status="info">
        Поля, отмеченные звёздочкой, обязательны для заполнения.
      </Notification>

      <div class="row mb-6">
        <div class="column-6 tb12 with-margin-bottom-24">
          <BasicInput v-model="form.fullName" class="mb-6" is-required label-text="Ф. И. О. (полностью)" :max-length="255" />

          <BasicInput v-model="form.city" class="mb-6" is-required label-text="Город" :max-length="255" />

          <BasicInput
            v-model="form.email"
            class="mb-6"
            is-required
            label-text="Email"
            :max-length="255"
          />

          <BasicInput v-model="form.phoneNumber" is-required label-text="Номер телефона" :max-length="255" />
        </div>
        <div class="column-6 tb12">
          <RadioButtons
            v-model="form.directionId"
            :choices="directionChoices"
            class="mb-6"
            is-required
            is-vertical
            label-text="Ваша сфера деятельности"
            name="directionId"
          />

          <BasicInput
            v-if="needToShowSubdirectionField"
            v-model="form.subdirection"
            class="mb-6"
            is-required
            :label-text="subdirectionFieldLabelText"
            :max-length="255"
          />

          <template v-if="!props.userData">
            <YesNoSwitch
              v-model="form.hasAccount"
              class="mb-6"
              :label-text="'Вы\xA0уже зарегистрированы на\xA0iTAB?'"
            />

            <Notification is-small status="info">
              <template v-if="form.hasAccount">
                Привяжем заявку к&nbsp;существующему аккаунту. Убедитесь, что указали тот&nbsp;же email, что и&nbsp;при регистрации.
              </template>
              <template v-else>
                Создадим аккаунт, отправив на&nbsp;указанный email письмо со&nbsp;ссылкой на&nbsp;подтверждение.
              </template>
            </Notification>

            <BasicInput
              v-if="!form.hasAccount"
              v-model="form.password"
              class="mt-6"
              is-password
              is-required
              label-text="Пароль"
            />
          </template>
        </div>
      </div>

      <hr class="mb-6">

      <LabelText
        class="mb-2"
        is-required
        :value="'Ваши каналы в\xA0социальных сетях (IG, Telegram, ВКонтакте, Одноклассники)'"
      />

      <div class="channels">
        <div v-for="(x, i) in form.channels" :key="i" class="channel">
          <div class="row mb-6">
            <div class="column-6 tb12 with-margin-bottom-24">
              <BasicInput v-model="x.link" is-required :label-text="'Ссылка на\xA0канал'" :max-length="255" />
            </div>
            <div class="column-6 tb12">
              <BasicInput
                v-model.number="x.subscribersCount"
                v-maska="maskaOptions.number"
                is-required
                :label-text="'Кол-во подписчиков на\xA0канале'"
                :max-length="255"
              />
            </div>
          </div>

          <BasicButton
            v-if="i !== 0"
            class="mb-6 ml-auto"
            color="red"
            is-small
            title="Удалить канал"
            @click="deleteChannel(i)"
          />
        </div>
      </div>

      <BasicButton
        v-if="form.channels.length < maxChannels"
        class="mb-6"
        color="body-background"
        is-small
        title="Добавить ещё один канал"
        @click="addChannel"
      />

      <hr class="mb-6">

      <div class="row mb-6">
        <div class="column-6 tb12 with-margin-bottom-24">
          <BasicInput
            v-model="form.desiredPromoCode"
            v-maska="maskaOptions.promoCode"
            class="mb-6"
            :description="'Используйте только латиницу, кириллицу и\xA0цифры. Регистр символов не\xA0учитывается.'"
            is-required
            label-text="Желаемый промокод"
            :max-length="255"
          />

          <BasicTextarea v-model="form.comment" label-text="Комментарий" :max-length="2048" />
        </div>
        <div class="column-6 tb12">
          <RadioButtons
            v-model="form.sourceId"
            :choices="sourceChoices"
            class="mb-6"
            is-required
            is-vertical
            :label-text="'Как вы узнали о\xA0нашей реферальной программе?'"
            name="sourceId"
          />

          <BasicInput
            v-if="needToShowCustomSourceField"
            v-model="form.customSource"
            :description="customSourceFieldLabelTextAndDescription[1]"
            is-required
            :label-text="customSourceFieldLabelTextAndDescription[0]"
            :max-length="255"
          />
        </div>
      </div>

      <BasicButton class="mb-6" color="green" :is-loading="isSending" :title="buttonTitle" @click="sendApplication" />

      <Agreement :button-title="buttonTitle" class="mb-0" need-to-agree-with-data-protection-policy />
    </template>

    <template #action>
      <a href="#" @click.prevent="close">Закрыть</a>
    </template>
  </CustomModalWindow>
</template>

<style lang="scss" scoped>
@use 'sass:color';

@use 'assets/css/variables';

.channels > .channel {
  border-bottom: 1px solid color.mix(variables.$main-gray-color, white, 20%);
  margin-bottom: 20px;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
}
</style>
